<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Liquidaciones</h1>
    </div>

    <!-- Cards resumen -->
    <div class="row mb-3">
      <div class="col-6 col-lg-3 mb-2" v-for="c in cards" :key="c.key">
        <div class="summary-card" :class="c.key">
          <span class="summary-card__label">{{ c.label }}</span>
          <span class="summary-card__value">{{ c.value }}</span>
        </div>
      </div>
    </div>

    <!-- Alertas de viajes sin liquidar -->
    <div v-if="dashboard.alerts && dashboard.alerts.length" class="alerts-box">
      <div class="alerts-title">
        <i class="ri-alarm-warning-line"></i>
        Proveedores con viajes sin liquidar
      </div>
      <div v-for="a in dashboard.alerts" :key="a.provider_id" class="alert-row">
        <span class="alert-name">{{ a.provider_name }}</span>
        <span class="text-muted">
          {{ a.pending_trips }} viaje(s) · {{ dashboard.currency }} {{ formatAmount(a.pending_amount) }} ·
          el más antiguo del {{ a.oldest_trip_date }} ({{ a.days_overdue }} días)
        </span>
        <button type="button" class="button button-light ml-auto" @click="openCreate(a.provider_id)">
          Liquidar
        </button>
      </div>
    </div>

    <div class="table-container">
      <div class="table-header">
        <div class="w-full d-flex flex-wrap align-items-end">
          <div class="filter-field mr-2 mb-2">
            <label class="filter-label">Proveedor</label>
            <select class="toolbar-select" v-model="filters.provider_id" @change="loadData">
              <option :value="null">Todos</option>
              <option v-for="p in providers" :key="p.id" :value="p.id">
                {{ p.business_name || p.contact_name }}
              </option>
            </select>
          </div>
          <div class="filter-field mr-2 mb-2">
            <label class="filter-label">Estado</label>
            <select class="toolbar-select" v-model="filters.status" @change="loadData">
              <option :value="null">Todos</option>
              <option v-for="s in statusOptions" :key="s.key" :value="s.key">{{ s.label }}</option>
            </select>
          </div>
          <div class="filter-field mr-2 mb-2">
            <label class="filter-label">Desde</label>
            <input type="date" class="toolbar-select" v-model="filters.from" @change="loadData" />
          </div>
          <div class="filter-field mr-2 mb-2">
            <label class="filter-label">Hasta</label>
            <input type="date" class="toolbar-select" v-model="filters.to" @change="loadData" />
          </div>
          <div class="ml-auto mb-2">
            <b-button variant="none" class="button button-primary" @click="openCreate(null)">
              Generar liquidación
            </b-button>
          </div>
        </div>
      </div>

      <div class="table-responsive mb-0 table-min-height">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th class="pl-4">Código</th>
              <th>Proveedor</th>
              <th class="text-center">Período</th>
              <th class="text-center">Viajes</th>
              <th class="text-center">Tarifas</th>
              <th class="text-center">Extras</th>
              <th class="text-center">Deducciones</th>
              <th class="text-center">Total</th>
              <th class="text-center">Estado</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="s in list" :key="s.id" class="clickable-row" @click="goToDetail(s.id)">
              <td class="pl-4 font-weight-bold">{{ s.settlement_code }}</td>
              <td>
                {{ s.provider_name }}
                <span :class="`badge ml-1 ${providerTypeMeta(s.provider_type).color}`">
                  {{ providerTypeMeta(s.provider_type).label }}
                </span>
              </td>
              <td class="text-center">
                <div class="small">{{ s.period_start }}</div>
                <div class="small text-muted">al {{ s.period_end }}</div>
              </td>
              <td class="text-center">{{ s.total_trips }}</td>
              <td class="text-center">{{ formatAmount(s.total_fare_amount) }}</td>
              <td class="text-center">{{ formatAmount(s.total_extras_amount) }}</td>
              <td class="text-center text-danger">
                {{ s.deductions_amount ? "-" + formatAmount(s.deductions_amount) : "-" }}
              </td>
              <td class="text-center font-weight-bold">{{ s.currency }} {{ formatAmount(s.final_amount) }}</td>
              <td class="text-center">
                <span :class="`badge ${statusMeta(s.settlement_status).color}`">
                  {{ statusMeta(s.settlement_status).label }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer d-flex justify-content-end align-items-center">
        <pagination
          :total="total"
          :page="page"
          :page-size="pageSize"
          :pages="pages"
          :changePage="changePage"
          :changePageSize="changePageSize"
          v-if="list.length"
        />
        <div class="table-no-results" v-else>
          <p class="m-0 px-3">No se encontraron liquidaciones.</p>
        </div>
      </div>
    </div>

    <!-- Generar liquidación -->
    <b-modal id="create-settlement-modal" title="Generar liquidación" hide-footer size="md" @hidden="resetCreate">
      <div class="custom-form">
        <div class="form-group">
          <label class="label">Proveedor *</label>
          <select class="custom-form-control" v-model="createForm.provider_id" @change="loadPending">
            <option :value="null">Elegir proveedor</option>
            <option v-for="p in providers" :key="p.id" :value="p.id">
              {{ p.business_name || p.contact_name }} ({{ frequencyLabel(p.payment_frequency) }})
            </option>
          </select>
        </div>

        <div class="row">
          <div class="col-md-6 form-group">
            <label class="label">Desde</label>
            <input type="date" class="custom-form-control" v-model="createForm.period_start" @change="loadPending" />
          </div>
          <div class="col-md-6 form-group">
            <label class="label">Hasta</label>
            <input type="date" class="custom-form-control" v-model="createForm.period_end" @change="loadPending" />
          </div>
        </div>
        <p class="text-muted small">
          Sin fechas se usa el período que corresponde a la frecuencia de pago del proveedor.
        </p>

        <div v-if="pending" class="pending-box">
          <div>
            Período <strong>{{ pending.period_start }}</strong> al <strong>{{ pending.period_end }}</strong>
          </div>
          <div v-if="pending.trips.length">
            <strong>{{ pending.trips.length }}</strong> viaje(s) por liquidar ·
            <strong>{{ pending.currency }} {{ formatAmount(pending.total_amount) }}</strong>
          </div>
          <div v-else class="text-muted">No hay viajes finalizados sin liquidar en ese período.</div>
        </div>

        <div class="form-group mt-2">
          <label class="label">Notas</label>
          <textarea class="custom-form-control" rows="2" v-model="createForm.notes" maxlength="500"></textarea>
        </div>

        <div class="d-flex justify-content-end mt-3">
          <b-button variant="none" class="button button-light mr-2" @click="$bvModal.hide('create-settlement-modal')">
            Cancelar
          </b-button>
          <b-button variant="none" class="button button-primary" :disabled="!canCreate || creating" @click="submitCreate">
            {{ creating ? "Generando..." : "Generar" }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Alert, Util, PaginationMixin } from "wize-admin";

import { SettlementService, ProviderService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

// Listado de liquidaciones con cards y alertas (DEF-ADM-021 §5.1).
export default {
  name: "SettlementList",
  mixins: [PaginationMixin],
  data() {
    return {
      list: [],
      providers: [],
      dashboard: {
        drafts: 0,
        pending_confirmation: 0,
        pending_payment: 0,
        paid_this_month: 0,
        currency: "PEN",
        alerts: [],
      },
      filters: { provider_id: null, status: null, from: "", to: "" },
      pageSize: 10,
      createForm: { provider_id: null, period_start: "", period_end: "", notes: "" },
      pending: null,
      creating: false,
    };
  },
  computed: {
    statusOptions() {
      return Object.values(LocalConstants.SettlementStatus);
    },
    canCreate() {
      return !!this.createForm.provider_id && !!this.pending && this.pending.trips.length > 0;
    },
    // Mismas cards que Gastos de flota: etiqueta arriba, valor grande y franja
    // de color a la izquierda.
    cards() {
      return [
        { key: "drafts", label: "Borradores", value: this.dashboard.drafts },
        {
          key: "confirmation",
          label: "Pendientes de confirmación",
          value: this.dashboard.pending_confirmation,
        },
        { key: "payment", label: "Pendientes de pago", value: this.dashboard.pending_payment },
        {
          key: "paid",
          label: "Pagado este mes",
          value: `${this.dashboard.currency} ${this.formatAmount(this.dashboard.paid_this_month)}`,
        },
      ];
    },
  },
  methods: {
    formatAmount(v) {
      return Number(v || 0).toFixed(2);
    },
    statusMeta(key) {
      return Util.searchValue(key, LocalConstants.SettlementStatus) || { label: key, color: "badge-gray" };
    },
    providerTypeMeta(key) {
      return Util.searchValue(key, LocalConstants.ProviderType) || { label: key, color: "badge-gray" };
    },
    frequencyLabel(key) {
      const meta = LocalConstants.PaymentFrequency[key];
      return meta ? meta.label : key;
    },
    async loadData() {
      try {
        await this.$store.dispatch("app/loading", true);
        const params = { page: this.page - 1, size: this.pageSize };
        Object.keys(this.filters).forEach((k) => {
          if (this.filters[k]) params[k] = this.filters[k];
        });
        const res = await SettlementService.list(params);
        this.list = res.payload.items || [];
        this.total = res.payload.metadata ? res.payload.metadata.totalElements : 0;
        this.pages = res.payload.metadata ? res.payload.metadata.totalPages : 1;
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudieron cargar las liquidaciones");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    async loadDashboard() {
      try {
        const res = await SettlementService.dashboard();
        this.dashboard = res.payload;
      } catch (ex) {
        console.error(ex);
      }
    },
    async loadProviders() {
      try {
        const res = await ProviderService.list({ status: 1 }, { page: 0, size: 200 });
        this.providers = res.payload.items || [];
      } catch (ex) {
        console.error(ex);
        this.providers = [];
      }
    },
    goToDetail(id) {
      this.$router.push({ name: "settlements-detail", params: { id } });
    },
    openCreate(providerId) {
      this.resetCreate();
      this.createForm.provider_id = providerId;
      if (providerId) this.loadPending();
      this.$bvModal.show("create-settlement-modal");
    },
    // Previsualiza qué viajes entrarían antes de generar.
    async loadPending() {
      this.pending = null;
      if (!this.createForm.provider_id) return;
      try {
        const res = await SettlementService.pendingTrips(
          this.createForm.provider_id,
          this.createForm.period_start || undefined,
          this.createForm.period_end || undefined
        );
        this.pending = res.payload;
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudieron cargar los viajes del período");
      }
    },
    async submitCreate() {
      this.creating = true;
      try {
        const res = await SettlementService.create({
          provider_id: Number(this.createForm.provider_id),
          period_start: this.createForm.period_start || null,
          period_end: this.createForm.period_end || null,
          notes: this.createForm.notes || null,
        });
        Alert.success("Liquidación generada");
        this.$bvModal.hide("create-settlement-modal");
        this.$router.push({ name: "settlements-detail", params: { id: res.payload.id } });
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo generar la liquidación");
      } finally {
        this.creating = false;
      }
    },
    resetCreate() {
      this.createForm = { provider_id: null, period_start: "", period_end: "", notes: "" };
      this.pending = null;
    },
  },
  async mounted() {
    await Promise.all([this.loadData(), this.loadDashboard(), this.loadProviders()]);
  },
};
</script>

<style lang="stylus" scoped>
// Cards con el mismo formato que las de Gastos de flota.
.summary-card
  background #fff
  border 1px solid #e5e7eb
  border-radius 10px
  padding 14px 16px
  display flex
  flex-direction column
  height 100%
  &.drafts
    border-left 4px solid #8592A6
  &.confirmation
    border-left 4px solid #fd7e14
  &.payment
    border-left 4px solid #dc3545
  &.paid
    border-left 4px solid #28a745
.summary-card__label
  font-size 11px
  text-transform uppercase
  letter-spacing .05em
  color #8592A6
.summary-card__value
  font-size 20px
  font-weight 700
  margin-top 4px
.alerts-box
  background #fff3cd
  border 1px solid #ffeaa7
  border-radius 6px
  padding 12px 16px
  margin-bottom 16px
.alerts-title
  font-weight 600
  color #856404
  margin-bottom 8px
.alert-row
  display flex
  align-items center
  gap 8px
  flex-wrap wrap
  padding 4px 0
.alert-name
  font-weight 600
// Fila de filtros con el mismo aspecto que la toolbar de Viajes.
.filter-field
  display flex
  flex-direction column
.filter-label
  font-size 11px
  color #6c757d
  margin-bottom 2px
.toolbar-select
  height 34px
  padding 0 10px
  font-size 0.8rem
  border 1px solid #d1d5db
  border-radius 6px
  background-color #fff
  color #374151
  cursor pointer
  &:focus
    outline none
    border-color #3b82f6
.clickable-row
  cursor pointer
.pending-box
  background #f8f9fa
  border-radius 6px
  padding 12px
  margin-top 8px
</style>
